import { Link } from 'react-router-dom';
import { Utensils, Phone, MapPin, Clock, Mail, Instagram, Facebook, MessageCircle } from 'lucide-react';
import { useSiteSettings } from '../../services/settingsService';

// TikTok icon component (Lucide doesn't have TikTok, so we create a custom SVG)
function TikTokIcon({ size = 18, className = '' }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
    </svg>
  );
}

/**
 * Professional footer — pulls live data from admin site_settings.
 */
export function Footer() {
  const { data: settings } = useSiteSettings();
  const currentYear = new Date().getFullYear();

  const name = settings?.restaurant_name || 'Smothers Restaurant';
  const tagline = settings?.tagline || 'Authentic. Tasty. Truly African.';
  const phone = settings?.whatsapp_phone || '254700000000';
  const email = settings?.email || null;
  const address = settings?.address || 'Nairobi, Kenya';
  const hours = settings?.opening_hours || 'Mon – Sun, 7:00 AM – 9:00 PM';

  // Social media links
  const socialLinks = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      url: `https://wa.me/${phone.replace(/\D/g, '')}`,
      color: 'hover:text-green-400',
    },
    {
      name: 'Instagram',
      icon: Instagram,
      url: 'https://www.instagram.com/smothers_dishes?igsh=MWcxZWkza2NzMXFzZg%3D%3D&utm_source=qr',
      color: 'hover:text-pink-400',
    },
    {
      name: 'Facebook',
      icon: Facebook,
      url: 'https://www.facebook.com/Smothersdishes',
      color: 'hover:text-blue-400',
    },
    {
      name: 'TikTok',
      icon: TikTokIcon,
      url: 'https://www.tiktok.com/@smothers.restaurants',
      color: 'hover:text-white',
    },
  ];

  // Format phone for display: 254700000000 → +254 700 000 000
  const formatPhone = (p) => {
    if (!p) return '';
    const digits = p.replace(/\D/g, '');
    if (digits.length === 12) {
      return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
    }
    return `+${digits}`;
  };

  return (
    <footer className="px-4 sm:px-6 lg:px-8 pb-6 pt-10">
      <div className="max-w-5xl mx-auto bg-brand-dark rounded-2xl overflow-hidden">
        {/* Main content */}
        <div className="px-6 sm:px-10 lg:px-14 py-10 sm:py-12">
          {/* Brand — centered */}
          <div className="flex flex-col items-center text-center mb-10">
            <div className="flex items-center gap-2.5 mb-3">
              <Utensils size={20} className="text-brand-red" />
              <span className="text-lg font-extrabold text-brand-white tracking-tight">{name}</span>
            </div>
            <p className="text-sm text-gray-400 leading-relaxed max-w-xs mb-5">
              {tagline}
            </p>
            
            {/* Social Media Links */}
            <div className="flex items-center gap-3">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`w-10 h-10 flex items-center justify-center rounded-full bg-gray-800 text-gray-400 transition-all ${social.color} hover:bg-gray-700`}
                    aria-label={social.name}
                    title={social.name}
                  >
                    <Icon size={18} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Info columns — equal 3-col grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
            {/* Quick links */}
            <div>
              <h4 className="text-xs font-bold text-brand-white uppercase tracking-widest mb-3">
                Quick Links
              </h4>
              <ul className="space-y-2">
                {[
                  { label: 'Home', to: '/' },
                  { label: 'Menu', to: '/menu' },
                ].map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-sm text-gray-400 hover:text-brand-white transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-xs font-bold text-brand-white uppercase tracking-widest mb-3">
                Contact
              </h4>
              <ul className="space-y-2">
                <li>
                  <a
                    href={`tel:+${phone.replace(/\D/g, '')}`}
                    className="inline-flex items-start justify-center gap-2 text-sm text-gray-400 hover:text-brand-white transition-colors"
                  >
                    <Phone size={13} className="text-brand-red flex-shrink-0 mt-0.5" />
                    <span className="text-left">
                      {formatPhone(phone)}
                      <span className="block text-[10px] text-gray-500 mt-0.5">Main Line</span>
                    </span>
                  </a>
                </li>
                {settings?.additional_phones && settings.additional_phones.length > 0 && (
                  settings.additional_phones.map((additionalPhone, index) => (
                    <li key={index}>
                      <a
                        href={`tel:+${additionalPhone.number.replace(/\D/g, '')}`}
                        className="inline-flex items-start justify-center gap-2 text-sm text-gray-400 hover:text-brand-white transition-colors"
                      >
                        <Phone size={13} className="text-brand-red flex-shrink-0 mt-0.5" />
                        <span className="text-left">
                          {formatPhone(additionalPhone.number)}
                          {additionalPhone.label && (
                            <span className="block text-[10px] text-gray-500 mt-0.5">{additionalPhone.label}</span>
                          )}
                        </span>
                      </a>
                    </li>
                  ))
                )}
                {email && (
                  <li>
                    <a
                      href={`mailto:${email}`}
                      className="inline-flex items-start justify-center gap-2 text-sm text-gray-400 hover:text-brand-white transition-colors"
                    >
                      <Mail size={13} className="text-brand-red flex-shrink-0 mt-0.5" />
                      <span className="break-all">{email}</span>
                    </a>
                  </li>
                )}
                <li className="inline-flex items-start justify-center gap-2 text-sm text-gray-400">
                  <MapPin size={13} className="text-brand-red flex-shrink-0 mt-0.5" />
                  <span className="text-left">{address}</span>
                </li>
              </ul>
            </div>

            {/* Hours */}
            <div>
              <h4 className="text-xs font-bold text-brand-white uppercase tracking-widest mb-3">
                Opening Hours
              </h4>
              <div className="inline-flex items-start gap-2 text-sm text-gray-400">
                <Clock size={13} className="text-brand-red flex-shrink-0 mt-0.5" />
                <p className="leading-relaxed">{hours}</p>
              </div>
            </div>
          </div>

          {/* Locations */}
          <div className="mt-8 pt-8 border-t border-gray-700/50">
            <h4 className="text-xs font-bold text-brand-white uppercase tracking-widest mb-4 text-center">
              Our Locations
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <a
                href="https://www.google.com/maps/search/?api=1&query=Imperial+House+Moi+Avenue+Nairobi"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Imperial House location in Google Maps"
                className="bg-gray-800/50 rounded-xl p-4 text-center"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <MapPin size={14} className="text-brand-red" />
                  <p className="text-sm font-bold text-brand-white">Imperial House</p>
                </div>
                <p className="text-xs text-gray-400">Moi Avenue, Nairobi</p>
              </a>
              <a
                href="https://www.google.com/maps/search/?api=1&query=Eagle+House+Kimathi+Street+Nairobi"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Open Eagle House location in Google Maps"
                className="bg-gray-800/50 rounded-xl p-4 text-center"
              >
                <div className="flex items-center justify-center gap-2 mb-2">
                  <MapPin size={14} className="text-brand-red" />
                  <p className="text-sm font-bold text-brand-white">Eagle House</p>
                </div>
                <p className="text-xs text-gray-400">Kimathi Street, Nairobi</p>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-700/50">
          <div className="px-6 sm:px-10 lg:px-14 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-xs text-gray-500">
              &copy; {currentYear} {name}. All rights reserved.
            </p>
            <div className="flex items-center gap-5">
              <span className="text-xs text-gray-500 hover:text-gray-400 transition-colors cursor-pointer">Privacy Policy</span>
              <span className="text-xs text-gray-500 hover:text-gray-400 transition-colors cursor-pointer">Terms of Service</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
